import { DASHBOARD_MAP, TDashboard } from './constants';

// Icons.
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArticleIcon from '@mui/icons-material/Article';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import { TChartType } from 'components/Dashboard/shared/types';
import type { TWidget } from 'utility/types/dataProvider';
import type { TPostWidgetPayload } from 'utility/types/dashboard';

export const getDashboardLink = ({
  baseUrl,
  dashboardData,
}: {
  baseUrl: string;
  dashboardData: TDashboard;
}) => {
  // Handle custom dashboards
  if (dashboardData?.type === 'custom') {
    const dashboardId = dashboardData?.id;
    if (dashboardId) {
      return `${baseUrl}/dashboards/view/${dashboardId}`;
    }
    // If no ID, return '#' to prevent navigation
    return '#';
  }

  // System dashboards - original logic
  const { app = '', page = '' } = dashboardData?.location || {};
  const appPath = DASHBOARD_MAP.app[app as keyof typeof DASHBOARD_MAP.app];
  const pagePath = DASHBOARD_MAP.page[page as keyof typeof DASHBOARD_MAP.page];

  // Home dashboard.
  if (app === 'core' && page === 'home') {
    return `${baseUrl}/`;
  }

  // Editorial asset.
  if (appPath === 'editorial' && pagePath === 'asset') {
    return '#';
  }

  // Home dashboards.
  if (page === 'home') {
    return `${baseUrl}/${appPath}/dashboard/`;
  }

  // TODO: CMSADMIN-1704: Special case for subscription reports page.
  if (appPath === 'user' && page === 'reports') {
    return '#';
  }

  // subscription.search -> user/account/search
  if (appPath === 'user' && pagePath === 'search') {
    return `${baseUrl}/user/account/search/`;
  }

  // Valid app and page navigates to the dashboard.
  if (appPath && pagePath) {
    return `${baseUrl}/${appPath}${pagePath !== '/' ? `/${pagePath}/` : ''}`;
  }

  // Default error log and no link.
  return '#';
};

export const getDisplayTypeIcon = (displayType?: string) => {
  const props = { color: 'info' as const, fontSize: 'small' as const };

  switch (displayType as TChartType) {
    case 'kpi':
      return <ShowChartIcon {...props} />;

    case 'pie':
      return <PieChartIcon {...props} />;

    case 'table':
      return <TableChartIcon {...props} />;

    case 'line':
      return <TimelineIcon {...props} />;

    case 'bar':
      return <BarChartIcon {...props} />;

    case 'area':
      return <DonutSmallIcon {...props} />;

    case 'funnel':
      return <FilterListIcon {...props} />;

    case 'detailscard':
      return <ArticleIcon {...props} />;

    case 'bubble':
      return <BubbleChartIcon {...props} />;

    default:
      return <BarChartIcon {...props} />;
  }
};

/**
 * Normalizes a widget into a payload format expected by the API for creating/updating widgets.
 * Extracts the essential properties (app, module, parameters, display) from a widget object.
 *
 * @param widget - The widget to normalize
 * @returns A normalized widget payload ready for API submission
 * @throws Error if widget is missing required app or module ID
 */
export const normalizeWidgetPayload = (widget: TWidget): TPostWidgetPayload => {
  // Extract app and module ID from widget structure
  const app = widget.app || widget.module?.app;
  const moduleId = widget.module?.id;

  if (!app || !moduleId) {
    throw new Error('Invalid widget structure: missing app or module ID');
  }

  return {
    title: widget.title || 'Untitled Widget',
    app: app,
    module: moduleId,
    parameters: widget?.parameters,
    display: {
      view: widget?.display?.view,
      options: widget?.display?.options || {},
    },
  };
};
